/**
 * BANT lifecycle logic for curated contact uploads.
 *
 * A contact must clear at least one BANT letter (Budget / Authority / Need /
 * Timeline) at upload time to land in the "active" lifecycle state. Contacts
 * with no BANT signals enter "holding" — they live in the system but are NOT
 * cadence-eligible and NOT counted in pipeline.
 *
 * Lifecycle:
 *   holding  → at upload time when zero BANT letters pass.
 *              Soft-killed at day 14 if no BANT signal is added.
 *              Hard-killed at day 30.
 *   active   → at upload time when ≥ 1 BANT letter passes.
 *
 * The cron-based enforcement of soft/hard kill is wired separately. This file
 * is the single source of truth for "which letters pass" so the upload UI,
 * holding view, and any downstream evaluator stay consistent.
 */

export const HOLDING_SOFT_KILL_DAYS = 14;
export const HOLDING_HARD_KILL_DAYS = 30;

export type BantLetter = "B" | "A" | "N" | "T";

/** A single normalized contact row, post-mapping, pre-insert. */
export interface ContactRow {
  first_name: string;
  last_name: string;
  email: string;
  company: string;
  title: string;
  phone?: string;
  linkedin_url?: string;
  source?: string;
  notes?: string;
  budget_signal?: string;
  authority_role?: string;
  need_evidence?: string;
  timeline_signal?: string;
  assigned_to?: string;
}

/** Required columns for any upload — these must be non-empty on every row. */
export const REQUIRED_COLUMNS = [
  "first_name",
  "last_name",
  "email",
  "company",
  "title",
] as const;

/** BANT signal columns — at least one of these populated promotes to active. */
export const BANT_COLUMNS = [
  "budget_signal",
  "authority_role",
  "need_evidence",
  "timeline_signal",
] as const;

/** Optional informational columns. */
export const OPTIONAL_COLUMNS = [
  "phone",
  "linkedin_url",
  "source",
  "notes",
  "assigned_to",
] as const;

/** Full set of platform columns the upload template ships with. */
export const PLATFORM_COLUMNS = [
  ...REQUIRED_COLUMNS,
  ...BANT_COLUMNS,
  ...OPTIONAL_COLUMNS,
] as const;

export type PlatformColumn = (typeof PLATFORM_COLUMNS)[number];

/**
 * evaluateBant — returns the BANT letters that pass for a row.
 * A letter passes if the corresponding signal column is non-empty after trim.
 */
export function evaluateBant(row: Partial<ContactRow>): BantLetter[] {
  const out: BantLetter[] = [];
  if ((row.budget_signal ?? "").trim()) out.push("B");
  if ((row.authority_role ?? "").trim()) out.push("A");
  if ((row.need_evidence ?? "").trim()) out.push("N");
  if ((row.timeline_signal ?? "").trim()) out.push("T");
  return out;
}

/** Lifecycle decision at upload time, given BANT letters. */
export function lifecycleStateForLetters(letters: BantLetter[]): "active" | "holding" {
  return letters.length > 0 ? "active" : "holding";
}

/**
 * Validate row shape — checks required columns and email format. Returns a
 * list of human-readable reasons; empty array means the row is acceptable.
 * Rows missing BANT are NOT rejected — they enter holding.
 */
export function validateRow(row: Partial<ContactRow>): string[] {
  const reasons: string[] = [];
  for (const col of REQUIRED_COLUMNS) {
    if (!(row[col] ?? "").trim()) reasons.push(`missing ${col}`);
  }
  const email = (row.email ?? "").trim();
  if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
    reasons.push("invalid email");
  }
  return reasons;
}

/**
 * Compute days remaining before soft-kill, given holding_started_at.
 * Negative means already eligible for soft-kill.
 * FENCE-FIX: accept null/undefined so HoldingRow never throws a TypeError
 * when holding_started_at is missing from a contact row.
 */
export function holdingDaysRemaining(holdingStartedAt: string | Date | null | undefined): number {
  // Guard: null/undefined/invalid dates fall back to 0 days remaining
  if (!holdingStartedAt) return 0;
  const raw = typeof holdingStartedAt === "string"
    ? new Date(holdingStartedAt).getTime()
    : holdingStartedAt.getTime();
  if (isNaN(raw)) return 0;
  const elapsedMs = Date.now() - raw;
  const elapsedDays = elapsedMs / (1000 * 60 * 60 * 24);
  return Math.max(0, Math.ceil(HOLDING_SOFT_KILL_DAYS - elapsedDays));
}

/**
 * Color band for the holding-day countdown badge.
 *  red    < 3 days remaining
 *  amber  3-7 days
 *  gray   8-14 days
 */
export function holdingCountdownTone(daysRemaining: number): "red" | "amber" | "gray" {
  if (daysRemaining < 3) return "red";
  if (daysRemaining <= 7) return "amber";
  return "gray";
}
